import { CalendarRange, Download, Users, X } from 'lucide-react';
import { useState } from 'react';

import { useAmbientesControllerListar } from '@/api/generated/admin-ambientes/admin-ambientes';
import { useRelatoriosControllerUsoPorUsuario } from '@/api/generated/admin-relatorios/admin-relatorios';
import {
  FiltroDeUf,
  TODAS_AS_UFS,
  type ValorDoFiltroDeUf,
} from '@/features/admin/relatorios/components/FiltroDeUf';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { ExportarCsvDialogo } from '@/shared/components/ExportarCsvDialogo';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * "Uso por usuário" — contagem de pacientes/exames/requisições/receituários/
 * discussões que cada usuário já gerou. Dado que já existia (nenhuma tabela
 * nova); a novidade é só agregar e mostrar. "Logins" é o total do histórico
 * (web e app) e fica fora do "Total", que soma só registros.
 *
 * O período recorta pela DATA DE CADASTRO do usuário (o mesmo filtro do
 * export "por período"). Os dois lados são opcionais e, sem nenhum, a lista é
 * de todos os cadastros — nunca um padrão invisível como "últimos 30 dias".
 * Molde do par de datas: `analises/components/SeletorDePeriodo`.
 */
export function UsoPorUsuarioTab() {
  const { pode } = usePermissoes();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [ambienteId, setAmbienteId] = useState<string>(TODOS);
  const [uf, setUf] = useState<ValorDoFiltroDeUf>(TODAS_AS_UFS);
  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');
  const [exportando, setExportando] = useState(false);

  const temPeriodo = Boolean(desde || ate);
  const ufFiltrada = uf !== TODAS_AS_UFS ? uf : undefined;

  const { data, isPending, isError, refetch } = useRelatoriosControllerUsoPorUsuario({
    pagina,
    porPagina: 20,
    ordenarPor: 'nomeCompleto',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(ambienteId !== TODOS ? { tenantId: ambienteId } : {}),
    ...(ufFiltrada ? { uf: ufFiltrada } : {}),
    ...(desde ? { desde } : {}),
    ...(ate ? { ate } : {}),
  });

  const { data: ambientes } = useAmbientesControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc' },
    { query: { enabled: pode('admin_tenants.visualizar') } },
  );

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por nome ou e-mail..."
            className="sm:max-w-xs"
          />

          {ambientes && (
            <Select value={ambienteId} onValueChange={(v) => aoFiltrar(() => setAmbienteId(v))}>
              <SelectTrigger className="sm:w-52" aria-label="Filtrar por ambiente">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos os ambientes</SelectItem>
                {ambientes.itens.map((ambiente) => (
                  <SelectItem key={ambiente.id} value={ambiente.id}>
                    {ambiente.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <FiltroDeUf valor={uf} aoMudar={(v) => aoFiltrar(() => setUf(v))} />

          <div className="flex flex-wrap items-center gap-2">
            <CalendarRange className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-sm text-muted-foreground">Cadastrados de</span>
            <Input
              type="date"
              value={desde}
              onChange={(e) => aoFiltrar(() => setDesde(e.target.value))}
              aria-label="Data de cadastro inicial"
              className="w-40"
              max={ate || undefined}
            />
            <span className="text-sm text-muted-foreground">até</span>
            <Input
              type="date"
              value={ate}
              onChange={(e) => aoFiltrar(() => setAte(e.target.value))}
              aria-label="Data de cadastro final"
              className="w-40"
              min={desde || undefined}
            />
            {temPeriodo ? (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1"
                onClick={() =>
                  aoFiltrar(() => {
                    setDesde('');
                    setAte('');
                  })
                }
              >
                <X className="w-3.5 h-3.5" />
                Limpar
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">Sem período: todos os cadastros.</span>
            )}
          </div>
        </div>

        <Button variant="outline" size="sm" className="gap-2" onClick={() => setExportando(true)}>
          <Download className="w-4 h-4" />
          Exportar
        </Button>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Users}
            titulo="Nenhum usuário encontrado"
            descricao={
              temPeriodo
                ? 'Nenhum usuário cadastrado no período escolhido bate com os filtros.'
                : 'Nenhum usuário bate com os filtros escolhidos.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Ambiente</TableHead>
                    <TableHead>UF</TableHead>
                    <TableHead>Data de cadastro</TableHead>
                    <TableHead className="text-right">Pacientes</TableHead>
                    <TableHead className="text-right">Exames</TableHead>
                    <TableHead className="text-right">Requisições</TableHead>
                    <TableHead className="text-right">Receituários</TableHead>
                    <TableHead className="text-right">Discussões</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Logins</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <span className="font-medium text-foreground">{item.nomeCompleto}</span>
                        <p className="text-xs text-muted-foreground mt-0.5">{item.email}</p>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.ambienteNome ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{item.uf ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {new Date(item.criadoEm).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-right text-foreground">{item.pacientes}</TableCell>
                      <TableCell className="text-right text-foreground">{item.exames}</TableCell>
                      <TableCell className="text-right text-foreground">
                        {item.requisicoes}
                      </TableCell>
                      <TableCell className="text-right text-foreground">
                        {item.receituarios}
                      </TableCell>
                      <TableCell className="text-right text-foreground">
                        {item.discussoes}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-brand">
                        {item.totalRegistros}
                      </TableCell>
                      <TableCell className="text-right text-foreground">{item.logins}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <ExportarCsvDialogo
        aberto={exportando}
        aoFechar={() => setExportando(false)}
        titulo="Exportar uso por usuário"
        caminho="/v1/admin/relatorios/uso-por-usuario/exportar.csv"
        descricaoCompleto="Exporta TODOS os usuários e suas contagens, ignorando busca, ambiente, estado e qualquer período."
        descricaoPeriodo="Aplica o recorte da tela (busca, ambiente e estado) e limita aos usuários criados no intervalo (os dois lados são opcionais)."
        filtros={{
          busca: busca.trim() || undefined,
          tenantId: ambienteId !== TODOS ? ambienteId : undefined,
          uf: ufFiltrada,
        }}
      />
    </div>
  );
}
